import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import JsonLd from '@/components/feature/JsonLd';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { dramas } from '@/mocks/dramas';
import type { Drama } from '@/types/content';
import DramaHero from './components/DramaHero';
import NextEpisodeSection from './components/NextEpisodeSection';
import EpisodeGuide from './components/EpisodeGuide';
import SynopsisSection from './components/SynopsisSection';
import RelatedDramas from './components/RelatedDramas';

const allDramas = dramas as Drama[];
const SITE_ORIGIN = 'https://jinghuibt.cn';

export default function DramaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const drama = allDramas.find((item) => item.id === id);

  useDocumentMeta({
    title: drama
      ? `《${drama.title}》分集剧情介绍+追剧日历，连续剧集更新顺序与下一集预告 - 爆料短剧`
      : '短剧未找到 - 爆料短剧',
    description: drama
      ? `《${drama.title}》${drama.status}，已更新至第${drama.updatedEpisodes}集。查看连续剧集更新顺序、每集剧情分集介绍、下一集预告与全季剧情梗概，按顺序追剧不漏更。`
      : '没有找到对应的短剧，返回剧集库浏览全部在追短剧。',
    keywords: drama
      ? `${drama.title},${drama.title}分集剧情,${drama.title}更新顺序,追剧日历,下一集预告`
      : '短剧,剧集库',
    path: `/drama/${id ?? ''}`,
    ogType: 'video.tv_show',
  });

  if (!drama) {
    return (
      <SiteLayout>
        <section className="mx-auto flex w-full max-w-content flex-col items-center justify-center px-4 py-24 text-center md:px-6 md:py-32">
          <i className="ri-emotion-sad-line text-5xl text-background-400" aria-hidden="true" />
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这部剧</h1>
          <p className="mt-3 max-w-md text-sm leading-7 text-foreground-600">
            这部剧可能还没有被收录，或者链接有误。回到剧集库看看其他在追的短剧吧。
          </p>
          <Link
            to="/dramas"
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-play-list-2-line" aria-hidden="true" />
            返回剧集库
          </Link>
        </section>
      </SiteLayout>
    );
  }

  const seriesSchema = {
    '@context': 'https://schema.org',
    '@type': 'TVSeries',
    name: drama.title,
    alternateName: drama.subtitle,
    description: drama.synopsis,
    genre: drama.category,
    inLanguage: 'zh-CN',
    numberOfEpisodes: drama.totalEpisodes,
    url: `${SITE_ORIGIN}/drama/${drama.id}`,
    image: drama.cover,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: drama.rating,
      bestRating: 10,
      ratingCount: Math.round(drama.heat / 100),
    },
    subjectOf: {
      '@type': 'CreativeWorkSeason',
      numberOfEpisodes: drama.updatedEpisodes,
    },
  };

  const episodeSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${drama.title} 分集剧情介绍`,
    itemListElement: drama.episodes.map((episode, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'TVEpisode',
        episodeNumber: episode.no,
        name: episode.title,
        description: episode.summary,
        datePublished: episode.date,
        partOfSeries: { '@type': 'TVSeries', name: drama.title },
      },
    })),
  };

  return (
    <SiteLayout>
      <JsonLd data={seriesSchema} />
      <JsonLd data={episodeSchema} />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 py-4 md:px-6">
          <Breadcrumbs items={[{ label: '剧集库', to: '/dramas' }, { label: drama.title }]} />
        </div>
      </div>

      <DramaHero drama={drama} />
      <NextEpisodeSection drama={drama} />
      <EpisodeGuide drama={drama} />
      <SynopsisSection drama={drama} />
      <RelatedDramas current={drama} all={allDramas} />
    </SiteLayout>
  );
}